import React, { FC, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Elements
import {
  AgeValidationModal,
  CheckBox,
  ConfirmationModal,
  AdditionalCheckButton,
  Heading,
  Input,
  RadioInput,
  Submit,
  SubmitType,
  AdditionalCheckModal,
  TextArea,
} from "../FormElements";
import { WaitWhileSummary } from "../Summary/WaitWhileSummary";

// Helpers
import { onSubmit, setStorage } from "../Helpers/formFunctions";
import { thirdStepValidator } from "../Validation/ThirdStepValidator";
import { isObjectEmpty } from "../Helpers";

// Types
import {
  FieldsSize,
  FromThirdStepData,
  RootDataType,
} from "../Helpers/interfaces";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../Helpers/formTypes";
import { CreateBookingResponseDto } from "@apiModels/CreateBookingResponseDto.csharp";

// Hooks
import {
  useThirdStepConfirm,
  useThirdStepDropInSubmit,
  useThirdStepSubmit,
  useThirdStepSubmitMetodika,
} from "../Hooks/useStepData";
import { useLabels } from "../Hooks/useLabels";
import { RichTextArea } from "../RichTextArea/RichTextArea";
import { GtmService } from "../GtmService";
import { FieldStatus } from "../Helpers/FieldStatus";
import { StorageAccessor } from "../StorageAccesor/StorageAccesor";
import { BookingStatus } from "../../../../Api/Model/BookingStatus.csharp";
import { ErrorMessage } from "../NoAppointments/ErrorMessage";
import { LoadingAnimation } from "../../LoadingAnimation/LoadingAnimation";
import { calculateTotalPrice } from "../Helpers/totalPriceCalculator";
import { useBookingContext } from "../../BookingButton/BookingContext/BookingContext";
import {
  isUnderage,
  thirdStepDataDropInParser,
  thirdStepDataCrateParser,
  thirdStepDataConfirmParser,
  isOfAge,
  minAgeNotBlocking,
  maxAgeNotBlocking,
} from "../Helpers/thirdStepHelpers";
import { AppointmentBookingSystem } from "@apiModels/AppointmentBookingSystem.csharp";
import { PhoneConfirmationModal } from "../FormElements/PopUps/PhoneConfirmationModal";

type ThirdStepProps = {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  rootData: RootDataType;
};

const defaultNotesLength = 400;

export const ThirdStep: FC<ThirdStepProps> = ({
  state,
  dispatch,
  nextStep,
  rootData,
}) => {
  const [isSMSModalOpen, setIsSMSModalOpen] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const [
    isPreparationRequirementsModalOpen,
    setIsPreparationRequirementsModalOpen,
  ] = useState<boolean>(false);
  const [
    isDoctorPreparationRequirementsOpen,
    setIsDoctorPreparationRequirementsOpen,
  ] = useState<boolean>(false);
  const [isAgeValidationModalOpen, setIsAgeValidationModalOpen] =
    useState<boolean>(false);
  const [isPhoneConfirmationModalOpen, setIsPhoneConfirmationModalOpen] =
    useState<boolean>(false);

  const [dropInAPIResponse, setDropInAPIResponse] =
    useState<CreateBookingResponseDto>();

  const { waitlist, awaitTime } = useBookingContext();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { bookingLabels } = useLabels();
  const thirdStepLabels = bookingLabels!.stepsBookingLabels.thirdStepLabels;

  const dropdownLabels = thirdStepLabels.dropdownLabels;

  const remoteContact = waitlist?.remoteContact || state.remoteContact;

  const hasRemoteOptions =
    !!remoteContact?.appointmentOptions.length &&
    (state.service.enableVideoTelephoneOption || !!waitlist?.remoteContact);

  const formDefaultValues: Partial<FromThirdStepData> = {
    remoteContact: remoteContact.appointmentOptions.find(
      (opt) => opt.preselected
    )?.label,
    preparationRequirements:
      !!state.appointment.preparationRequirementsDto &&
      state.appointment.preparationRequirementsDto.preselected,
    doctorPreparationRequirements:
      !!state.appointment.doctorPreparationRequirementsDto &&
      state.appointment.doctorPreparationRequirementsDto.preselected,
  };

  const [bookingLoading, setBookingLoading] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationError, setConfirmationError] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [requireParentPersonalIdNumber, setRequireParentPersonalIdNumber] =
    useState(false);
  const [requirePreparationRequirements, setRequirePreparationRequirements] =
    useState(false);
  const [notesLength, setNotesLength] = useState(defaultNotesLength);
  const [ageValidationModalDescription, setAgeValidationModalDescription] =
    useState<string | undefined>(undefined);

  useEffect(() => {
    Modal.setAppElement(wrapperRef.current!);
  }, []);

  useEffect(() => {
    if (!waitlist) {
      setStorage(state);
    }
  }, [state]);

  const { requireParentSsn, blockedSsnList, blockedSsnErrorMessage } =
    useBookingContext();

  const {
    handleSubmit,
    getValues,
    formState: { errors },
    register,
    setValue,
    watch,
  } = useForm<FromThirdStepData>({
    defaultValues: formDefaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(
      thirdStepValidator(
        state,
        bookingLabels!.bookingErrors,
        waitlist,
        requireParentPersonalIdNumber,
        thirdStepLabels,
        requirePreparationRequirements,
        blockedSsnList,
        blockedSsnErrorMessage,
        hasRemoteOptions
      )
    ),
  });

  useEffect(() => {
    const personalNumber = `${getValues().personalIdNumber}`;
    const minMaxAge = state.appointment.minMaxAge;
    const minMaxAgeNotBlocking =
      minMaxAge == undefined ||
      (minAgeNotBlocking(personalNumber, minMaxAge) &&
        maxAgeNotBlocking(personalNumber, minMaxAge));
    if (
      !!state.appointment.preparationRequirementsDto &&
      !minMaxAge?.showPreparationRequirementsOnly
    )
      setRequirePreparationRequirements(true);
    else setRequirePreparationRequirements(false);

    if (
      requireParentSsn &&
      isUnderage(personalNumber) &&
      minMaxAgeNotBlocking
    ) {
      setRequireParentPersonalIdNumber(true);
      setNotesLength(370);
    } else {
      setRequireParentPersonalIdNumber(false);
      setNotesLength(defaultNotesLength);
    }

    if (minMaxAge?.showPreparationRequirementsOnly)
      setRequirePreparationRequirements(shouldShowPreparationRequirement());
  }, [getValues, state.appointment, watch("personalIdNumber")]);

  const thirdStepSubmit = async (props: FromThirdStepData) => {
    GtmService.bookingSubmitThirdStep(
      state.specialization.name,
      state.specialization.id,
      state.service.bookingName,
      state.service.id,
      state.location.name,
      state.location.id,
      state.doctor.name,
      state.doctor.id,
      state.appointment.startTime,
      props.subscribeToNewsletter,
      calculateTotalPrice(state.pricingSummary)
    );

    const minimumAge = state.appointment.minMaxAge?.minAge ?? 0;
    const minAgeShowWarning = state.appointment.minMaxAge?.showWarningOnly;
    const maximumAge = state.appointment.minMaxAge?.maxAge ?? 150;
    const maxAgeShowWarning = state.appointment.minMaxAge?.showWarningOnly;
    if (
      minAgeShowWarning &&
      isUnderage(`${props.personalIdNumber}`, minimumAge)
    ) {
      setAgeValidationModalDescription(
        state.appointment.minMaxAge?.minAgeModal?.description
      );
      setIsAgeValidationModalOpen(true);
      return;
    } else if (
      maxAgeShowWarning &&
      isOfAge(`${props.personalIdNumber}`, maximumAge)
    ) {
      setAgeValidationModalDescription(
        state.appointment.minMaxAge?.maxAgeModal?.description
      );
      setIsAgeValidationModalOpen(true);
      return;
    } else {
      await createBooking(props);
      GtmService.smsRequested();
    }
  };
  
  const createMetodikaBooking = async (props: FromThirdStepData) => {
    const remoteOption = hasRemoteOptions
      ? remoteContact.appointmentOptions.find(
          (opt) => opt.label.toLowerCase() == props.remoteContact
        )
      : undefined;
    const bookingRequest = thirdStepDataCrateParser(
      props,
      state,
      requireParentPersonalIdNumber,
      remoteOption
    );

    try {
      setBookingLoading(true);
      const bookingResponse = await useThirdStepSubmitMetodika(bookingRequest);
      setBookingLoading(false);
      dispatch({
        type: FormReducer.update,
        field: FormFields.bookingResponse,
        payload: bookingResponse,
      });
      onSubmit(nextStep, errors);
      clearStorage();
      GtmService.orderConfirmed(
        state,
        rootData.centers.find((c) => c.id == state.appointment.centerId)
      );
    } catch (err: any) {
      setBookingLoading(false);
      setBookingError(err.message);
      GtmService.bookingCreationError(
        state.service.name,
        state.specialization.name,
        state.location.name,
        err.message
      );
    }

    setConfirmationLoading(false);
  };

  const createBooking = async (props: FromThirdStepData) => {
    setIsAgeValidationModalOpen(false);
    setConfirmationError("");

    if (isObjectEmpty(errors) == false) return;

    setConfirmationLoading(true);

    const remoteOption = hasRemoteOptions
      ? remoteContact.appointmentOptions.find(
          (opt) => opt.label.toLowerCase() == props.remoteContact
        )
      : undefined;
    if (waitlist?.contentId) {
      const dropInRequest = thirdStepDataDropInParser(
        props,
        waitlist?.contentId,
        requireParentPersonalIdNumber,
        remoteOption
      );
      const dropInResponse = await useThirdStepDropInSubmit(dropInRequest);

      if (dropInResponse.bookingStatus == BookingStatus.Pending) {
        setDropInAPIResponse(dropInResponse);
        dispatch({
          type: FormReducer.update,
          field: FormFields.bookingSessionId,
          payload: dropInResponse.bookingSessionId,
        });
        setIsSMSModalOpen(true);
      }
    } else {
      const bookingRequest = thirdStepDataCrateParser(
        props,
        state,
        requireParentPersonalIdNumber,
        remoteOption
      );
      try {
        if (
          state.appointment.appointmentBookingSystem ==
          AppointmentBookingSystem.Dips
        ) {
          const bookingResponse = await useThirdStepSubmit(bookingRequest);
          if (bookingResponse.bookingStatus == BookingStatus.Pending) {
            dispatch({
              type: FormReducer.update,
              field: FormFields.bookingSessionId,
              payload: bookingResponse.bookingSessionId,
            });
            setIsSMSModalOpen(true);
          }
        } else if (
          state.appointment.appointmentBookingSystem ==
          AppointmentBookingSystem.Metodika
        ) {
          setIsPhoneConfirmationModalOpen(true);
          return;
        }
      } catch (err: any) {
        setBookingError(err.message);
        GtmService.bookingCreationError(
          state.service.name,
          state.specialization.name,
          state.location.name,
          err.message
        );
      }
    }
    setConfirmationLoading(false);
  };

  const closeSmsModal = () => {
    setIsSMSModalOpen(false);
    setConfirmationLoading(false);
  };

  async function confirmBooking(code?: number) {
    if (code) {
      let request;
      const props = getValues();

      const remoteOption = hasRemoteOptions
        ? remoteContact.appointmentOptions.find(
            (opt) => opt.label.toLowerCase() == props.remoteContact
          )
        : undefined;
      if (waitlist?.contentId) {
        request = thirdStepDataDropInParser(
          props,
          waitlist?.contentId,
          requireParentPersonalIdNumber,
          remoteOption,
          code,
          dropInAPIResponse
        );
      } else {
        request = thirdStepDataConfirmParser(
          props,
          state,
          requireParentPersonalIdNumber,
          remoteOption,
          code
        );
      }

      try {
        setBookingLoading(true);
        const response = await useThirdStepConfirm(request);
        setBookingLoading(false);

        if (response.bookingStatus == BookingStatus.Confirmed) {
          dispatch({
            type: FormReducer.update,
            field: FormFields.bookingResponse,
            payload: response,
          });

          onSubmit(nextStep, errors);
          clearStorage();

          if (waitlist?.contentId) {
            GtmService.dropInConfirmed(
              waitlist.serviceName,
              waitlist.centerName,
              state.bookingResponse.bookingSessionId
            );
          } else {
            GtmService.orderConfirmed(
              state,
              rootData.centers.find((c) => c.id == state.appointment.centerId)
            );
          }
        }
        if (
          response.bookingStatus == BookingStatus.Pending ||
          response.bookingStatus == BookingStatus.Invalid
        ) {
          setConfirmationError(response.message);
        }
      } catch (err: any) {
        setConfirmationError(err.message);
        setBookingLoading(false);
        GtmService.confirmationError(
          state.service.name,
          state.specialization.name,
          state.location.name,
          err.message
        );
      }
    }
  }

  const clearStorage = () => {
    new StorageAccessor().removeItem();
  };

  const shouldShowPreparationRequirement = () => {
    const ssn = getValues("personalIdNumber");
    const preparationRequirementsAvailable =
      !!state.appointment.preparationRequirementsDto;

    if (
      state.appointment.minMaxAge?.showPreparationRequirementsOnly &&
      preparationRequirementsAvailable
    ) {
      return (
        isUnderage(`${ssn}`, state.appointment.minMaxAge?.minAge || 0) ||
        isOfAge(`${ssn}`, state.appointment.minMaxAge?.maxAge || 150)
      );
    }

    return preparationRequirementsAvailable;
  };

  const determinedHeading = waitlist?.contentId
    ? "Dine opplysninger"
    : thirdStepLabels?.subHeading;

  const determineButtonLabel = waitlist?.contentId
    ? "Fortsett"
    : thirdStepLabels?.nextButtonLabel;

  return (
    <>
      <div ref={wrapperRef} className="mb-3">
        <Heading>{determinedHeading}</Heading>

        {!waitlist?.contentId && (
          <RichTextArea
            innerClassName="mb-3 booking-narrow-item"
            content={thirdStepLabels?.optionalDescription}
          />
        )}
        <form
          onSubmit={handleSubmit(thirdStepSubmit)}
          className="d-flex flex-column"
        >
          {state.appointment.appointmentBookingSystem ===
          AppointmentBookingSystem.Dips ? (
            <Input
              name="name"
              label={dropdownLabels.name}
              getValues={getValues}
              error={errors.name}
              register={register}
            />
          ) : (
            <>
              <Input
                name="firstName"
                label={dropdownLabels.firstName}
                getValues={getValues}
                error={errors.firstName}
                register={register}
              />
              <Input
                name="lastName"
                label={dropdownLabels.lastName}
                getValues={getValues}
                error={errors.lastName}
                register={register}
              />
            </>
          )}

          <Input
            name="personalIdNumber"
            label={dropdownLabels.personalIdNumber}
            inputmode={"numeric"}
            type="number"
            getValues={getValues}
            error={errors.personalIdNumber}
            register={register}
          />
          {requireParentPersonalIdNumber && (
            <Input
              name="parentPersonalIdNumber"
              label={dropdownLabels.parentPersonalIdNumber}
              inputmode={"numeric"}
              type="number"
              getValues={getValues}
              error={errors.parentPersonalIdNumber}
              register={register}
            />
          )}
          <Input
            name="mobileNumber"
            label={dropdownLabels.mobileNumber}
            inputmode={"tel"}
            type="tel"
            size={FieldsSize.twoThirdsWidth}
            getValues={getValues}
            error={errors.mobileNumber}
            register={register}
          />

          <Input
            name="emailAddress"
            label={dropdownLabels.emailAddress}
            inputmode={"email"}
            type="email"
            getValues={getValues}
            error={errors.emailAddress}
            register={register}
          />
          {hasRemoteOptions && (
            <RadioInput
              getValues={getValues}
              name="remoteContact"
              label={remoteContact.label}
              options={remoteContact.appointmentOptions}
              register={register}
              error={errors.remoteContact}
            />
          )}
          <TextArea
            label={
              !state.appointment.notesLabel
                ? dropdownLabels.notes
                : state.appointment.notesLabel
            }
            name="notes"
            error={errors.notes}
            register={register}
            maxLength={notesLength}
          />
          <CheckBox
            name="subscribeToNewsletter"
            label={dropdownLabels.subscribeToNewsletter}
            error={errors.subscribeToNewsletter}
            register={register}
          />
          {!waitlist?.contentId && (
            <CheckBox
              label=""
              name="terms"
              error={errors.terms}
              register={register}
            >
              <AdditionalCheckButton
                label={dropdownLabels.termsLabel}
                buttonLabel={
                  dropdownLabels.termsButton ?? "bestillingsvilkårene"
                }
                onClickFunction={() => setIsTermsModalOpen(true)}
              />
            </CheckBox>
          )}

          {dropdownLabels.consent && (
            <CheckBox
              name="consent"
              label={dropdownLabels.consent}
              error={errors.consent}
              register={register}
            />
          )}
          {shouldShowPreparationRequirement() && (
            <CheckBox
              label=""
              name="preparationRequirements"
              error={errors.preparationRequirements}
              register={register}
            >
              <AdditionalCheckButton
                label={state.appointment.preparationRequirementsDto!.label}
                buttonLabel={
                  state.appointment.preparationRequirementsDto!.button
                }
                onClickFunction={() =>
                  setIsPreparationRequirementsModalOpen(true)
                }
              />
            </CheckBox>
          )}
          {!!state.appointment.doctorPreparationRequirementsDto &&
            !!state.appointment.doctorPreparationRequirementsDto.label && (
              <CheckBox
                label=""
                name="doctorPreparationRequirements"
                register={register}
                error={errors.doctorPreparationRequirements}
              >
                <AdditionalCheckButton
                  label={
                    state.appointment.doctorPreparationRequirementsDto.label
                  }
                  buttonLabel={
                    state.appointment.doctorPreparationRequirementsDto.button
                  }
                  onClickFunction={() =>
                    setIsDoctorPreparationRequirementsOpen(true)
                  }
                />
              </CheckBox>
            )}
          {bookingError && <ErrorMessage message={bookingError} />}
          <Submit
            type={SubmitType.submit}
            status={
              confirmationLoading && !bookingError
                ? FieldStatus.disabled
                : FieldStatus.active
            }
          >
            {confirmationLoading ? (
              <LoadingAnimation text={determineButtonLabel} />
            ) : (
              determineButtonLabel
            )}
          </Submit>
        </form>
        {waitlist?.contentId && (
          <div className="mt-4">
            <WaitWhileSummary waitlist={waitlist} awaitTime={awaitTime} />
          </div>
        )}
        {isSMSModalOpen && (
          <ConfirmationModal
            onClose={closeSmsModal}
            Modal={Modal}
            isOpen={isSMSModalOpen}
            onSubmit={confirmBooking}
            loading={bookingLoading}
            confirmationError={confirmationError}
            heading={
              !waitlist?.contentId
                ? thirdStepLabels.smsConfirmationHeading
                : "Bekreft registreringen din"
            }
            content={thirdStepLabels.smsConfirmation.replace(
              "$sms",
              `${getValues("mobileNumber")}`
            )}
            inputLabel={thirdStepLabels.smsInputLabel}
            buttonLabel={thirdStepLabels.smsConfirmationButton}
          />
        )}
        {isPhoneConfirmationModalOpen && (
          <PhoneConfirmationModal
            createMetodikaBooking={createMetodikaBooking}
            onClose={() => {
              setIsPhoneConfirmationModalOpen(false);
              setConfirmationLoading(false);
            }}
            isOpen={isPhoneConfirmationModalOpen}
            getFormData={getValues}
            setValue={setValue}
            heading={thirdStepLabels.confirmPhoneNumberHeading}
            inputLabel={thirdStepLabels.confirmPhoneNumberLabel}
            intro={thirdStepLabels.confirmPhoneNumberIntro}
            buttonLabel={thirdStepLabels.confirmPhoneNumberButton}
            loading={bookingLoading}
            Modal={Modal}
          />
        )}
        <AdditionalCheckModal
          onClose={() => setIsTermsModalOpen(false)}
          isOpen={isTermsModalOpen}
          Modal={Modal}
          policy={thirdStepLabels.terms}
        />
        <AdditionalCheckModal
          onClose={() => setIsPreparationRequirementsModalOpen(false)}
          isOpen={isPreparationRequirementsModalOpen}
          Modal={Modal}
          policy={state.appointment.preparationRequirementsDto?.content ?? ""}
        />
        <AdditionalCheckModal
          onClose={() => setIsDoctorPreparationRequirementsOpen(false)}
          isOpen={isDoctorPreparationRequirementsOpen}
          Modal={Modal}
          policy={
            state.appointment.doctorPreparationRequirementsDto?.content ?? ""
          }
        />
        {state.appointment.minMaxAge?.showWarningOnly && (
          <AgeValidationModal
            Modal={Modal}
            onConfirm={handleSubmit(createBooking)}
            onClose={() => setIsAgeValidationModalOpen(false)}
            isOpen={isAgeValidationModalOpen}
            description={ageValidationModalDescription}
          />
        )}
      </div>
    </>
  );
};
